// src/pages/TrackCreation/Step2.tsx
import React, { useState, useEffect, useRef } from "react";
import { MapContainer, Polyline, TileLayer } from "react-leaflet";
import { useTranslation } from "react-i18next";
import { Map as LeafletMap } from "leaflet";
import { MarkerType, StopsHOHOProps } from "../../../../types/screensTypes";
import { getStopsForTrack, saveStop, getGeocode, getHohoInfo, deleteStopAPI, saveHohoInfo } from "../../../../api";
import TitleSubtitle from "../../../../components/common/TitleSubtitle";
import WaypointList from "../../../../components/lists/WaypointList";
import CustomMarker from "../../../../components/map/MapMarker";
import TextInput from "../../../../components/ui/TextInput";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import { useGlobalState } from "../../../../context/useGlobalState";
import { Waypoint } from "../../../../types/types";
import Button from "../../../../components/ui/Button";
import ToggleSwitch from "../../../../components/common/ToggleSwitch";
import { MapInitializer } from "../../../../utils/mapInitializer";
import shell from "../createShell.module.css";
import mapStyles from "../createMap.module.css";
import stepStyles from "../createStep.module.css";

const StopsHOHO: React.FC<StopsHOHOProps> = ({ navigateToNext, navigateToContinue }) => {
    const { trackData } = useTrackCreation();
    const { data } = useGlobalState();
    const [address] = useState<string>("");
    const [coordinates, setCoordinates] = useState<string>("");
    const [position, setPosition] = useState<string>("");
    const [stops, setStops] = useState<Waypoint[]>([]);
    const [cityName, setCityName] = useState<string | null>(null);
    const [map, setMap] = useState<LeafletMap | null>(null);
    const [loadingSave, setLoadingSave] = useState<boolean>(false);
    const [display, setDisplay] = useState<boolean | null>(null);
    // Guards persistence so we never overwrite the stored value before it loads.
    const hasLoaded = useRef<boolean>(false);
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    useEffect(() => {
        if (trackData && trackData.trackId) {
            getStopsTrackById(trackData.trackId);
        }
    }, [trackData.trackId]);

    useEffect(() => {
        if (map && stops && stops.length > 0) {
            const bounds = stops.map((waypoint) => [waypoint.point.lat, waypoint.point.lng]) as [number, number][];
            map.fitBounds(bounds);
        }
    }, [map, stops]);

    useEffect(() => {
        const cities = data?.locationCityOptions || [];
        const trackCity = trackData?.city;
        const cityFilter = cities.filter((city) => city.value === trackCity);
        const city = cityFilter.length > 0 ? cityFilter[0] : null;
        if (city) {
            setCityName(city.label);
        }
    }, [data, trackData]);

    const getStopsTrackById = async (id: string): Promise<void> => {
        const token = localStorage.getItem("token");
        if (!token) return;

        const responseData = await getStopsForTrack({ token, trackId: id });
        if (responseData) {
            const wpts = responseData.waypoints;
            setStops(wpts);
        }
    };

    const handleMarkerDragEnd = async (newPosition: { lat: number; lng: number }, id: string): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!token || !trackId) return;
        const { lat, lng } = newPosition;

        await saveStop({
            token,
            trackId,
            waypointId: id,
            latitude: lat,
            longitude: lng,
        });
        await getStopsTrackById(trackId);
    };

    const handleAddWaypoint = async (): Promise<void> => {
        setLoadingSave(true);
        let latitude: string = "";
        let longitude: string = "";

        if (coordinates.trim() !== "") {
            const parts = coordinates.split(",");
            if (parts.length >= 2) {
                latitude = parts[0].trim();
                longitude = parts[1].trim();
            }
        } else if (address.trim() !== "") {
            const geocode = await getGeocode({
                address: address + " " + (cityName || ""),
            });
            if (geocode) {
                latitude = String(geocode.lat).trim();
                longitude = String(geocode.lon).trim();
            } else {
                window.alert(t("addressNotFound"));
            }
        }

        try {
            if (latitude && longitude && position && trackData.trackId) {
                const token = localStorage.getItem("token");
                const trackId = trackData.trackId;
                if (!token || !trackId) return;
                await saveStop({
                    token,
                    trackId,
                    waypointId: null,
                    latitude: parseFloat(latitude),
                    longitude: parseFloat(longitude),
                    address,
                    position: parseInt(position),
                });
                await getStopsTrackById(trackData.trackId);
                setLoadingSave(false);
                // navigateToNext(temporaryId) // if needed
            } else {
                setLoadingSave(false);
            }
        } catch (error) {
            console.log(error);
            setLoadingSave(false);
        }
    };

    const saveHohoInfoAPI = async (nextDisplay: boolean): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!token || !trackId) return;

        await saveHohoInfo({
            token,
            trackId,
            isDisplay: nextDisplay,
        });
    };

    // Toggling activation is an explicit user action: update state and persist
    // the new value immediately, rather than relying on an effect that could
    // race with the initial load and reset the stored value.
    const handleToggleTracking = (isChecked: boolean): void => {
        setDisplay(isChecked);
        if (!hasLoaded.current) return;
        saveHohoInfoAPI(isChecked);
    };

    useEffect(() => {
        if (trackData && trackData.trackId) {
            getHohoInfoAPI(trackData.trackId);
        }
        return () => {
            hasLoaded.current = false;
        };
    }, [trackData.trackId]);

    const getHohoInfoAPI = async (trackId: string): Promise<void> => {
        const token = localStorage.getItem("token");
        if (!token) return;
        const ret = await getHohoInfo({ token, trackId });
        if (ret) {
            setDisplay(ret.track?.isBusTracking ?? false);
        }
        hasLoaded.current = true;
    };

    const deleteWaypoint = async (id: string): Promise<void> => {
        setLoadingSave(true);
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!token || !trackId) return;
        try {
            await deleteStopAPI({ token, trackId, waypointId: id });
            await getStopsTrackById(trackId);
            setLoadingSave(false);
        } catch (error) {
            setLoadingSave(false);
        }
    };

    const handleMapReady = (mapInstance: L.Map) => {
        setMap(mapInstance);
    };

    return (
        <div className={`content-container experience-map ${shell.mapShell}`}>
            <div className={mapStyles.leftColumn}>
                <div className={`experience-map-head ${mapStyles.head}`}>
                    <div className={`page-title experience-map-title ${mapStyles.titleBlock}`}>
                        <TitleSubtitle
                            title={t("stops")}
                            subtitle={t("adjustTheStopsOfYourAudioTourHere")}
                            layout="map"
                        />
                    </div>
                    <div className={`experience-map-activate ${mapStyles.activate}`}>
                        <span className={`experience-map-activate-label ${mapStyles.activateLabel}`}>
                            {t("activate")}
                        </span>
                        <ToggleSwitch checked={display || false} onChange={handleToggleTracking} />
                    </div>
                </div>

                {display === true && (
                    <div className={mapStyles.mapContainer}>
                        <div className="map-placeholder">
                            <MapContainer
                                center={[1, 1]}
                                zoom={16}
                                style={{ height: "480px", width: "100%" }}
                                scrollWheelZoom={false}
                                doubleClickZoom={false}
                            >
                                <MapInitializer onMapReady={handleMapReady} />
                                <TileLayer
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                />
                                <Polyline
                                    positions={stops?.map((waypoint) =>
                                        waypoint && waypoint.point?.lat && waypoint.point?.lng
                                            ? [waypoint.point.lat, waypoint.point.lng]
                                            : [0, 0],
                                    )}
                                    pathOptions={{ color: "#1930d8", weight: 4, opacity: 0.85 }}
                                />

                                {stops?.map((waypoint) => {
                                    const markerType = [
                                        "GARDEN",
                                        "INVISIBLE",
                                        "MOBILE",
                                        "FIXED",
                                        "QUIZ",
                                        "SELECTED",
                                        "STOP",
                                    ].includes(waypoint.type)
                                        ? (waypoint.type as MarkerType)
                                        : undefined;

                                    return (
                                        <CustomMarker
                                            key={waypoint.id}
                                            id={waypoint.id}
                                            lat={waypoint?.point?.lat || 0}
                                            lng={waypoint?.point?.lng || 0}
                                            type={markerType}
                                            position={waypoint.position}
                                            onSelect={() => navigateToNext(waypoint.id)}
                                            onMarkerDragEnd={handleMarkerDragEnd}
                                        />
                                    );
                                })}
                            </MapContainer>
                        </div>
                    </div>
                )}

                {display === true && (
                    <div className={`experience-map-add ${mapStyles.add}`}>
                        <h3 className={`experience-map-add-title ${mapStyles.addTitle}`}>{t("addANewStop")}</h3>
                        <div className={`add-waypoint-form experience-map-add-fields ${mapStyles.addFields}`}>
                            <div className="add-waypoint-coordinates">
                                <TextInput
                                    placeholder={t("enterCoordinatesLatLng")}
                                    value={coordinates}
                                    onChange={(e) => setCoordinates(e.target.value)}
                                />
                            </div>
                            <TextInput
                                placeholder={t("enterPosition")}
                                value={position}
                                onChange={(e) => setPosition(e.target.value)}
                            />
                            <Button
                                label={loadingSave ? t("loading") : t("addStop")}
                                onClick={handleAddWaypoint}
                                variant="primary"
                                shape="pill"
                                className={`${stepStyles.continueBtn} ${mapStyles.mapBtn}`}
                            />
                        </div>
                    </div>
                )}
            </div>

            <div className={`right-sidebar experience-map-poi ${mapStyles.poiColumn}`}>
                {display === true && (
                    <div className="waypoints-list">
                        <div className={`waypoints-list-header ${mapStyles.waypointsHeader}`}>
                            <h3>{t("stops")}</h3>
                        </div>
                        <WaypointList
                            combinedWaypoints={stops}
                            setCombinedWaypoints={setStops}
                            saveStops={saveStop}
                            deleteWaypoint={(id: string) => deleteWaypoint(id)}
                            navigateToNext={navigateToNext}
                            isStop={true}
                            isLoading={false}
                        />
                    </div>
                )}
            </div>

            {navigateToContinue && (
                <div className={`${stepStyles.stepButton} ${stepStyles.footer} ${stepStyles.footerMap}`}>
                    <Button
                        label={t("continue")}
                        onClick={navigateToContinue}
                        variant="primary"
                        shape="pill"
                        className={`${stepStyles.continueBtn} ${mapStyles.mapBtn}`}
                    />
                </div>
            )}
        </div>
    );
};

export default StopsHOHO;
